import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { enqueueSnackbar } from 'notistack';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';
import { UploadDialogProps, UploadThingResponse } from 'src/interfaces/FileManager';
import useFileManagerStore from 'src/stores/fileManagerStore';

import CustomUploadComponent from './CustomUploadComponent';
import { UPLOAD_ENDPOINTS } from './resolveUploadEndpoint';
import UploadTabPanel from './UploadTabPanel';

function resolveInitialTabIndex(
  visibleEndpoints: AppFileRouterEndpoint[],
  initialEndpoint: UploadDialogProps['initialEndpoint'],
): number {
  if (visibleEndpoints.length === 0) {
    return 0;
  }

  if (initialEndpoint && initialEndpoint !== 'all') {
    const index = visibleEndpoints.indexOf(initialEndpoint);
    if (index >= 0) {
      return index;
    }
  }

  return 0;
}

const UploadDialog = ({
  open,
  onClose,
  initialEndpoint = 'image',
  allowedEndpoints = UPLOAD_ENDPOINTS,
}: UploadDialogProps) => {
  const { t } = useTranslation();
  const fetchFiles = useFileManagerStore(state => state.fetchFiles);
  const createFileFromUpload = useFileManagerStore(state => state.createFileFromUpload);
  const [tabIndex, setTabIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);

  const visibleEndpoints = useMemo(
    () => UPLOAD_ENDPOINTS.filter(endpoint => allowedEndpoints.includes(endpoint)),
    [allowedEndpoints],
  );

  const showTabs = visibleEndpoints.length > 1;
  const activeEndpoint = visibleEndpoints[tabIndex] ?? visibleEndpoints[0] ?? 'image';

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (open) {
      setTabIndex(resolveInitialTabIndex(visibleEndpoints, initialEndpoint));
      setResetKey(prev => prev + 1);
    }
  }, [open, initialEndpoint, visibleEndpoints]);

  useEffect(() => {
    setResetKey(prev => prev + 1);
  }, [tabIndex]);

  const handleUploadComplete = async (res: UploadThingResponse[], endpoint: AppFileRouterEndpoint) => {
    if (!res || res.length === 0) return;

    try {
      const createPromises = res.map(file =>
        createFileFromUpload({
          fileName: file.name,
          fileUrl: file.url,
          fileKey: file.key,
          fileSize: file.size,
          uploadEndpoint: endpoint,
        }),
      );
      await Promise.all(createPromises);
      enqueueSnackbar(t('form.upload.success'), { variant: 'success' });
      fetchFiles();
    } catch {
      enqueueSnackbar(t('form.upload.error'), { variant: 'error' });
    }
  };

  const handleUploadError = (error: Error) => {
    enqueueSnackbar(error.message || t('form.upload.error'), { variant: 'error' });
  };

  const renderUploadPanel = (endpoint: AppFileRouterEndpoint) => (
    <CustomUploadComponent
      key={`${endpoint}-${resetKey}`}
      endpoint={endpoint}
      onUploadComplete={res => handleUploadComplete(res, endpoint)}
      onUploadError={handleUploadError}
      onReset={() => setResetKey(prev => prev + 1)}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('form.upload.label')}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {showTabs ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                aria-label={t('system.fileManager.fileType')}
              >
                {visibleEndpoints.map((endpoint, index) => (
                  <Tab
                    key={endpoint}
                    label={t(`system.fileManager.types.${endpoint}`)}
                    id={`upload-tab-${index}`}
                    aria-controls={`upload-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>
            {visibleEndpoints.map((endpoint, index) => (
              <UploadTabPanel key={endpoint} value={tabIndex} index={index}>
                {renderUploadPanel(endpoint)}
              </UploadTabPanel>
            ))}
          </>
        ) : (
          <Box sx={{ p: 2 }}>{renderUploadPanel(activeEndpoint)}</Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>
          {t('base.cancel')}
        </Button>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('base.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
